import { ElementRef, Inject, Injectable, Injector } from '@angular/core';

import { firstValueFrom, from, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';

import { AuthService } from './auth.service';
import { LoadScriptService } from '../../../services/load-script.service';
import { LocalStorageService } from '../../../services/local-storage.service'; // TEMPORARY
import { SignInResponse } from '../interfaces/sign-in-response';
import { WINDOW } from '../../../services/window.service';

@Injectable({
  providedIn: 'root'
})
export class GoogleSignInService {

  // Public ids
  public static readonly CLIENT_ID: string = '860022854282-gju1oofefi2c6rgp58aogl8allju4fmt.apps.googleusercontent.com';
  public static readonly PROVIDER_ID: string = 'google';

  // Configuration https://developers.google.com/identity/gsi/web/reference/js-reference#GsiButtonConfiguration
  BUTTON_LOCALE: string = '';
  BUTTON_LOGO_ALIGNMENT: 'left' | 'center' = 'left';
  BUTTON_SHAPE: 'square' | 'circle' | 'pill' | 'rectangular' = 'rectangular';
  BUTTON_SIZE: 'small' | 'medium' | 'large' = 'medium';
  BUTTON_TEXT: 'signin' | 'signin_with' | 'signup_with' | 'continue_with' = 'signin_with';
  BUTTON_THEME: 'outline' | 'filled_blue' | 'filled_black' = 'outline';
  BUTTON_TYPE: 'icon' | 'standard' = 'standard';
  BUTTON_WIDTH: string = '200';

  // Observable subjects
  private googleSignInSubject = new Subject<SignInResponse>();
  private initializationSubject = new ReplaySubject(1);

  constructor(
    private injector: Injector,
    private loadScriptService: LoadScriptService,
    private localStorageService: LocalStorageService,
    @Inject(WINDOW) private window: Window | null
  ) { }

  /**
   * Returns an observable that emits when the user signs in or out via Google.
   */
  getGoogleSignInObservable(): Observable<SignInResponse | null> {
    return this.googleSignInSubject.asObservable();
  }

  /**
   * Loads the Google Identity Services script and initializes the Google client.
   */
  initialize(): void {
    this.loadScriptService.loadScript(
      GoogleSignInService.PROVIDER_ID,
      'https://accounts.google.com/gsi/client',
      this.onScriptLoaded.bind(this)
    );
  }

  /**
   * Refresh the token using One Tap.
   * Prompts the Google One Tap UI and awaits a new credential or sign-out event.
   */
  async refreshToken(): Promise<SignInResponse | null> {

    // Ensure Google Identity Services is initialized
    await firstValueFrom(this.initializationSubject);

    // Prompt the user for credentials; waits for onSignInResponseCallback emission
    google.accounts.id.prompt();

    // Wait for the next sign-in or sign-out event
    return firstValueFrom(this.googleSignInSubject);
  }

  /**
   * Renders the Google Sign-In button inside the given element.
   * Accepts optional overrides for size, text and width.
   */
  renderButton(elem: ElementRef, configuration: any = null): void {
    firstValueFrom(this.initializationSubject).then(() => {
      google.accounts.id.renderButton(elem.nativeElement, {
        locale: this.BUTTON_LOCALE,
        logo_alignment: this.BUTTON_LOGO_ALIGNMENT,
        shape: this.BUTTON_SHAPE,
        size: configuration?.size || this.BUTTON_SIZE,
        text: configuration?.text || this.BUTTON_TEXT,
        theme: this.BUTTON_THEME,
        type: this.BUTTON_TYPE,
        width: configuration?.width || +this.BUTTON_WIDTH,
      });
    });
  }

  /**
   * Disables Google's auto-select and emits a sign-out event.
   * Returns an observable that completes once sign-out is processed.
   */
  signOut(): Observable<void> {
    return from(
      firstValueFrom(this.initializationSubject).then(() => {
        google.accounts.id.disableAutoSelect();
        this.googleSignInSubject.next(null);
      })
    );
  }

  /**
   * Decodes a JWT token payload without validation.
   * See: angularx-social-login google-login-provider implementation.
   */
  private decodeJwt(idToken: string): any {

    const base64Url = idToken.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      this.window.atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  }

  /**
   * Callback invoked by Google Identity Services upon sign-in.
   */
  private onSignInResponseCallback = ({ credential }: { credential: string }, onFailure: () => void) => {

    if (!credential) {
      onFailure();
      return;
    }

    const responsePayload = this.decodeJwt(credential);

    // Prepare external auth provider sign-in response object
    const signInResponse: SignInResponse = {
      Email: responsePayload.email,
      FirstName: responsePayload.given_name,
      LastName: responsePayload.family_name,
      Provider: GoogleSignInService.PROVIDER_ID,
      ProviderKey: responsePayload.sub,
      SessionInfo: null,
      Status: null,
      Token: credential
    };
    this.googleSignInSubject.next(signInResponse);
  }

  /**
   * Called when Google One Tap fails or is dismissed.
   * Emits a sign-out event and falls back to the standard login dialog.
   */
  private onSignInResponseFailure = () => {

    this.googleSignInSubject.next(null);

    // Fallback to AuthService login modal (skip refresh-session)
    const auth = this.injector.get(AuthService);
    auth.signIn('signin').catch(() => { });
  }

  // Once the script is loaded, start watching incoming credentials
  private onScriptLoaded() {
    google.accounts.id.initialize({
      client_id: GoogleSignInService.CLIENT_ID,
      auto_select: true,
      callback: (response) => this.onSignInResponseCallback(response, this.onSignInResponseFailure),
      cancel_on_tap_outside: false,
      // prompt_parent_id: this.initOptions?.prompt_parent_id,
      itp_support: true
    });

    this.initializationSubject.next(true);
  }
}
